import { useEffect, useState } from 'react';
import { APP_NAME } from '@/lib/brand';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Briefcase, Calendar, ChevronRight, MessageSquare, PhoneCall, Target, User, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

function QuickCard({ icon: Icon, title, value, subtitle, onClick }) {
  return (
    <button onClick={onClick} className="text-left">
      <Card className="bg-white/[0.03] border-white/10 hover:border-primary/30 transition-colors">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between"><Icon className="w-5 h-5 text-primary" /><ChevronRight className="w-4 h-4 text-white/20" /></div>
          <p className="text-lg font-semibold text-white">{value}</p>
          <p className="text-xs text-white/70">{title}</p>
          <p className="text-[10px] text-muted-foreground">{subtitle}</p>
        </CardContent>
      </Card>
    </button>
  );
}

export default function SellerMobileApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ leads: 0, opps: 0, convs: 0, appointments: 0 });
  const [assignedLeads, setAssignedLeads] = useState([]);
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    Promise.all([
      // paginacion-lista-leads: solo se muestran 6 en la tarjeta, pero
      // stats.leads necesita el TOTAL real (viene del backend, no de items.length).
      api.get('/leads', { params: { limit: 6 } }).catch(() => ({ data: { items: [], total: 0 } })),
      api.get('/opportunities').catch(() => ({ data: [] })),
      api.get('/ai-agent/conversations').catch(() => ({ data: [] })),
      api.get('/calendly/configs').catch(() => ({ data: [] })),
    ]).then(([leads, opps, convs, cal]) => {
      const leadItems = leads.data?.items || [];
      setAssignedLeads(leadItems.slice(0, 6));
      setConversations((convs.data || []).slice(0, 6));
      setStats({
        leads: leads.data?.total ?? leadItems.length,
        opps: (opps.data || []).length,
        convs: (convs.data || []).length,
        appointments: (cal.data || []).length,
      });
    });
  }, []);

  if (user?.role === 'admin' || user?.role === 'superadmin') {
    return <div className="min-h-screen bg-[#0a0c12] text-white flex items-center justify-center p-6 text-center">La app móvil PWA está enfocada para vendedores y atención comercial en campo.</div>;
  }

  return (
    <div className="min-h-screen bg-[#0a0c12] text-white pb-24" data-testid="seller-mobile-app">
      <div className="px-4 pt-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-primary">{APP_NAME} Seller App</p>
            <h1 className="text-2xl font-bold">Hola, {user?.name?.split(' ')[0]}</h1>
          </div>
          <Badge className="bg-white/5 border-white/10 text-white/70">{user?.role === 'guard' ? 'Guardia' : 'Vendedor'}</Badge>
        </div>

        <PWAInstallPrompt />

        <div className="grid grid-cols-2 gap-3">
          <QuickCard icon={Target} title="Leads asignados" value={stats.leads} subtitle="Tu bandeja comercial" onClick={() => navigate('/leads')} />
          <QuickCard icon={Briefcase} title="Oportunidades" value={stats.opps} subtitle="Pipeline del día" onClick={() => navigate('/opportunities')} />
          <QuickCard icon={MessageSquare} title="Conversaciones IA" value={stats.convs} subtitle="Takeover disponible" onClick={() => navigate('/ai-agent')} />
          <QuickCard icon={Calendar} title="Citas / agenda" value={stats.appointments} subtitle="Calendly y seguimiento" onClick={() => navigate('/settings')} />
        </div>

        <Card className="bg-white/[0.03] border-white/10">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2"><User className="w-4 h-4 text-primary" /><p className="text-sm font-semibold">Leads prioritarios</p></div>
            <div className="space-y-2">
              {assignedLeads.length === 0 ? <p className="text-xs text-muted-foreground">No tienes leads asignados ahora mismo.</p> : assignedLeads.map(lead => (
                <button key={lead.id} onClick={() => navigate('/leads')} className="w-full text-left p-3 rounded-xl bg-black/20 border border-white/5">
                  <div className="flex items-center justify-between gap-3"><p className="text-sm text-white font-medium">{lead.name}</p>{lead.ai_score ? <Badge className="bg-green-500/10 text-green-400 border-green-500/20">{lead.ai_score}/10</Badge> : null}</div>
                  <p className="text-[11px] text-white/60 mt-1">{lead.phone || lead.email || 'Sin contacto'}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/[0.03] border-white/10">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /><p className="text-sm font-semibold">Conversaciones activas</p></div>
            <div className="space-y-2">
              {conversations.length === 0 ? <p className="text-xs text-muted-foreground">No hay conversaciones activas.</p> : conversations.map(conv => (
                <button key={conv.id} onClick={() => navigate(`/ai-agent?conv=${conv.id}`)} className="w-full text-left p-3 rounded-xl bg-black/20 border border-white/5">
                  <div className="flex items-center justify-between gap-3"><p className="text-sm text-white font-medium">{conv.lead_name || conv.phone}</p><PhoneCall className="w-4 h-4 text-emerald-300" /></div>
                  <p className="text-[11px] text-white/60 mt-1">{conv.phone}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-white/10 bg-[#0a0c12]/95 backdrop-blur px-3 py-2 grid grid-cols-5 gap-2">
        {[['/mobile', 'Inicio'], ['/leads', 'Leads'], ['/opportunities', 'Pipeline'], ['/ai-agent', 'Chat'], ['/settings', 'Perfil']].map(([path, label]) => (
          <Button key={path} variant="ghost" className="text-[10px] text-white/70 hover:text-white h-12" onClick={() => path === '/mobile' ? navigate('/mobile') : navigate(path)}>{label}</Button>
        ))}
      </div>
    </div>
  );
}